import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ROLES = [
  { value: 'artist', label: 'Artiste', description: 'DJ, producteur, performer' },
  { value: 'promoter', label: 'Promoteur', description: 'Organisateur d\'événements' },
  { value: 'booker', label: 'Bookeur', description: 'Agent d\'artistes' },
];

export default function Register() {
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    role: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (!form.role) {
      setError('Choisis ton rôle');
      return;
    }

    setSubmitting(true);

    try {
      await register({
        email: form.email,
        password: form.password,
        displayName: form.displayName,
        role: form.role,
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center th-bg px-4 py-12 transition-colors duration-300">
      <div className="w-full max-w-sm animate-fadeInUp">
        <div className="text-center mb-8">
          <Link to="/" className="font-mono font-bold text-2xl text-accent tracking-[0.15em]">STRIDERR</Link>
          <p className="mt-2 text-sm th-text-secondary">Crée ton compte</p>
        </div>

        <form onSubmit={handleSubmit} className="border th-border p-6 space-y-6 th-bg-surface transition-colors duration-300">
          {error && (
            <div className="border border-status-rejected/30 text-status-rejected px-3 py-2 text-sm font-mono">
              {error}
            </div>
          )}

          {/* Sélection du rôle */}
          <div>
            <label className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
              Je suis...
            </label>
            <div className="grid grid-cols-3 gap-3">
              {ROLES.map((role) => (
                <button
                  key={role.value}
                  type="button"
                  onClick={() => updateField('role', role.value)}
                  className={`p-3 border text-left transition-colors duration-150 ${
                    form.role === role.value
                      ? 'border-accent bg-accent/10'
                      : 'th-border th-hover'
                  }`}
                >
                  <div className="font-mono text-sm font-medium th-text">{role.label}</div>
                  <div className="text-xs th-text-muted mt-0.5">{role.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="displayName" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
              Nom d'affichage
            </label>
            <input
              id="displayName"
              type="text"
              required
              value={form.displayName}
              onChange={(e) => updateField('displayName', e.target.value)}
              className="th-input"
              placeholder="Ton nom ou pseudo"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className="th-input"
              placeholder="ton@email.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              className="th-input"
              placeholder="8 caractères minimum"
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-xs font-mono th-text-secondary mb-2 uppercase tracking-widest">
              Confirmer le mot de passe
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={form.confirmPassword}
              onChange={(e) => updateField('confirmPassword', e.target.value)}
              className="th-input"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full th-btn-accent"
          >
            {submitting ? 'Inscription...' : 'Créer mon compte'}
          </button>
        </form>

        <p className="text-center mt-6 text-sm th-text-muted">
          Déjà un compte ?{' '}
          <Link to="/login" className="text-accent hover:opacity-70 transition-opacity duration-150 font-mono">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
}
